import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { FileText, Calendar, Mail, Phone, Building, MapPin, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockData } from './mock';

export const TermsOfService = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-12 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 hover:text-indigo-600 px-4 py-2 rounded-lg border border-gray-200 hover:border-indigo-300 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ArrowLeft size={16} />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-100 to-purple-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-indigo-200/50 backdrop-blur-sm">
            <FileText size={16} className="sm:w-4 sm:h-4 text-indigo-600" />
            <Badge className="bg-transparent text-indigo-700 px-0 py-0 text-xs sm:text-sm font-semibold">
              Legal Terms
            </Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Terms of Service
          </h1>
          <div className="flex items-center justify-center gap-4 text-sm sm:text-base text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="sm:w-4 sm:h-4" />
              <span>Effective Date: 02.10.2025</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-4 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Building size={16} className="sm:w-5 sm:h-5 text-white" />
                </div>
                <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  Traffic Service International LLC
                </CardTitle>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Building No: 915, office No: 579, CBD (Abu Dhabi bank) Al Falaj street, Muscat, Sultanate of Oman, Postal Code: 111
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Welcome</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
                  Welcome to Traffic Service International LLC ("Company", "we", "our", "us"). By accessing or using our website <a href="https://www.tsioman.com" className="text-indigo-600 hover:text-indigo-700 underline" target="_blank" rel="noopener noreferrer">www.tsioman.com</a> (the "Website"), you agree to comply with and be bound by these Terms of Service.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  If you do not agree, please do not use our website.
                </p>
              </section>

              {/* Terms Sections */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Terms and Conditions</h2>
                
                <div className="space-y-6 sm:space-y-8">
                  {/* 1. Use of Website */}
                  <div className="border-l-4 border-blue-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">1. Use of Website</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• You may use this Website for lawful purposes only.</li>
                      <li>• You agree not to use the Website in a way that could damage, disable, overburden, or impair its functionality.</li>
                      <li>• Unauthorized use may lead to legal action.</li>
                    </ul>
                  </div>

                  {/* 2. Intellectual Property */}
                  <div className="border-l-4 border-green-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">2. Intellectual Property</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• All content on this Website, including text, images, graphics, logos, videos, and software, is owned by Traffic Service International LLC or its licensors.</li>
                      <li>• No content may be copied, reproduced, distributed, or modified without prior written permission.</li>
                    </ul>
                  </div>

                  {/* 3. User Accounts */}
                  <div className="border-l-4 border-purple-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">3. User Accounts</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• Some parts of the Website may require creating an account.</li>
                      <li>• You are responsible for keeping your account credentials confidential.</li>
                      <li>• You must notify us immediately if you suspect unauthorized use of your account.</li>
                    </ul>
                  </div>

                  {/* 4. Privacy */}
                  <div className="border-l-4 border-yellow-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">4. Privacy</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• Our Privacy Policy explains how we collect, use, and protect your personal information.</li>
                      <li>• By using this Website, you consent to the practices described in our Privacy Policy.</li>
                    </ul>
                  </div>

                  {/* 5. Third-Party Links */}
                  <div className="border-l-4 border-red-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">5. Third-Party Links</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• The Website may include links to third-party websites.</li>
                      <li>• We are not responsible for the content, policies, or practices of these third-party sites.</li>
                      <li>• Linking does not imply endorsement by Traffic Service International LLC.</li>
                    </ul>
                  </div>

                  {/* 6. Disclaimers */}
                  <div className="border-l-4 border-indigo-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">6. Disclaimers</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• The Website is provided "as is" and "as available."</li>
                      <li>• We do not guarantee the accuracy, reliability, or availability of the Website or its content.</li>
                      <li>• Use of the Website is at your own risk.</li>
                    </ul>
                  </div>

                  {/* 7. Limitation of Liability */}
                  <div className="border-l-4 border-pink-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">7. Limitation of Liability</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• Traffic Service International LLC will not be liable for any direct, indirect, incidental, or consequential damages arising from your use of the Website.</li>
                      <li>• This includes any loss of data, profits, or other intangible losses.</li>
                    </ul>
                  </div>

                  {/* 8. Indemnification */}
                  <div className="border-l-4 border-teal-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">8. Indemnification</h3>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                      You agree to indemnify and hold Traffic Service International LLC, its directors, officers, employees, and affiliates harmless from any claims, liabilities, or expenses resulting from your use of the Website or violation of these Terms.
                    </p>
                  </div>

                  {/* 9. Termination */}
                  <div className="border-l-4 border-orange-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">9. Termination</h3>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                      We may suspend or terminate your access to the Website at any time without notice for conduct that violates these Terms or is harmful to the Website or its users.
                    </p>
                  </div>

                  {/* 10. Governing Law */}
                  <div className="border-l-4 border-cyan-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">10. Governing Law</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• These Terms of Service are governed by the laws of the Sultanate of Oman.</li>
                      <li>• Any disputes will be resolved in the courts of Oman.</li>
                    </ul>
                  </div>

                  {/* 11. Changes to Terms */}
                  <div className="border-l-4 border-emerald-500 pl-4 sm:pl-6">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">11. Changes to Terms</h3>
                    <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
                      <li>• We may update these Terms at any time.</li>
                      <li>• Revised Terms will be posted on the Website with the updated effective date.</li>
                      <li>• Continued use of the Website constitutes acceptance of the updated Terms.</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Contact Us</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
                  If you have any questions about these Terms of Service, please contact us:
                </p>
                
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 sm:p-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <Building size={16} className="sm:w-5 sm:h-5 text-indigo-600" />
                    <div>
                      <p className="text-indigo-700 font-semibold text-sm sm:text-base">Traffic Service International LLC</p>
                      <p className="text-gray-600 text-xs sm:text-sm">Muscat, Sultanate of Oman</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Mail size={16} className="sm:w-5 sm:h-5 text-indigo-600" />
                    <span className="text-indigo-700 font-medium text-sm sm:text-base">{mockData.contact.email}</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Phone size={16} className="sm:w-5 sm:h-5 text-indigo-600" />
                    <span className="text-indigo-700 font-medium text-sm sm:text-base">+968 95221926</span>
                  </div>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
